import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from '../config/firebase';

// Enhanced types matching the backend schema
export interface DataSourcePermissions {
  read: string[];
  write: string[];
  admin: string[];
}

export interface FirestoreConfig {
  projectId: string;
  collections?: string[];
}

export interface ShopifyConfig {
  shopDomain: string;
}

export interface GoogleAnalyticsConfig {
  propertyId: string;
  authType?: 'oauth2';
  refreshToken?: string;
}

export interface StripeConfig {
  publishableKey: string;
}

export type DataSourceConfiguration = FirestoreConfig | ShopifyConfig | GoogleAnalyticsConfig | StripeConfig;

export type DataSourceType = 'firestore' | 'shopify' | 'google-analytics' | 'facebook-ads' | 'stripe';
export type DataSourceStatus = 'connected' | 'active' | 'error' | 'pending' | 'disabled';
export type DataSourceVisibility = 'organization' | 'private';

export interface DataSourceSyncSettings {
  enabled: boolean;
  frequency: 'manual' | 'hourly' | 'daily' | 'weekly';
  lastSync?: Date;
  autoRefreshCharts: boolean;
}

export interface DataSourceUsageStats {
  chartsUsingSource: number;
  lastAccessed?: Date;
  totalQueries: number;
  dataVolumeProcessed: number;
}

export interface DataSource {
  id: string;
  organizationId: string;
  userId: string;
  visibility: DataSourceVisibility;
  permissions: DataSourcePermissions;
  name: string;
  description?: string;
  type: DataSourceType;
  status: DataSourceStatus;
  configuration: DataSourceConfiguration;
  syncSettings: DataSourceSyncSettings;
  tags?: string[];
  usageStats: DataSourceUsageStats;
  lastError?: string;
  errorCount: number;
  createdAt: Date;
  updatedAt: Date;
  lastTestedAt?: Date;
}

export interface CreateDataSourceRequest {
  name: string;
  description?: string;
  type: DataSourceType;
  visibility?: DataSourceVisibility;
  configuration: {
    projectId?: string;
    serviceAccountKey?: string;
    shopDomain?: string;
    accessToken?: string;
    propertyId?: string;
    publishableKey?: string;
    secretKey?: string;
  };
  syncSettings?: Partial<DataSourceSyncSettings>;
  tags?: string[];
  organizationId: string;
}

export interface CreateDataSourceResponse {
  success: boolean;
  dataSourceId: string;
  message: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface GetCollectionsResponse {
  success: boolean;
  collections: string[];
}

export interface SampleDataResponse {
  success: boolean;
  sampleData: any[];
  collectionName: string;
  documentCount: number;
}

export interface GetDataSourcesResponse {
  success: boolean;
  dataSources: DataSource[];
}

export interface DeleteDataSourceResponse {
  success: boolean;
  message: string;
}

export interface UpdateVisibilityRequest {
  dataSourceId: string;
  visibility?: DataSourceVisibility;
  permissions?: Partial<DataSourcePermissions>;
}

export interface UpdateVisibilityResponse {
  success: boolean;
  message: string;
}

export interface GoogleAnalyticsReportRequest {
  dataSourceId: string;
  dateRanges: { startDate: string; endDate: string }[];
  metrics: { name: string }[];
  dimensions: { name: string }[];
}

export interface GoogleAnalyticsReportResponse {
  success: boolean;
  report?: any;
  error?: string;
  message?: string;
}

// Service class for data source operations
export class DataSourcesService {
  private createDataSourceFn = httpsCallable(functions, 'createDataSource');
  private testConnectionFn = httpsCallable(functions, 'testDataSourceConnection');
  private getCollectionsFn = httpsCallable(functions, 'getDataSourceCollections');
  private getSampleDataFn = httpsCallable(functions, 'getCollectionSampleData');
  private listUserDataSourcesFn = httpsCallable(functions, 'listUserDataSources');
  private deleteDataSourceFn = httpsCallable(functions, 'deleteDataSource');
  private updateVisibilityFn = httpsCallable(functions, 'updateDataSourceVisibility');
  private getGoogleAnalyticsDataFn = httpsCallable(functions, 'getGoogleAnalyticsData');

  /**
   * Create a new data source connection
   */
  async createDataSource(request: CreateDataSourceRequest): Promise<CreateDataSourceResponse> {
    try {
      const result = await this.createDataSourceFn(request);
      return result.data as CreateDataSourceResponse;
    } catch (error: any) {
      console.error('Error creating data source:', error);
      throw new Error(error.message || 'Failed to create data source');
    }
  }

  /**
   * Test connection to a data source
   */
  async testConnection(dataSourceId: string): Promise<TestConnectionResponse> {
    try {
      const result = await this.testConnectionFn({ dataSourceId });
      return result.data as TestConnectionResponse;
    } catch (error: any) {
      console.error('Error testing connection:', error);
      throw new Error(error.message || 'Failed to test connection');
    }
  }

  /**
   * Get available collections from a Firestore data source
   */
  async getCollections(dataSourceId: string): Promise<GetCollectionsResponse> {
    try {
      const result = await this.getCollectionsFn({ dataSourceId });
      return result.data as GetCollectionsResponse;
    } catch (error: any) {
      console.error('Error getting collections:', error);
      throw new Error(error.message || 'Failed to get collections');
    }
  }

  /**
   * Get sample data from a collection
   */
  async getSampleData(dataSourceId: string, collectionName: string, limit: number = 10): Promise<SampleDataResponse> {
    try {
      const result = await this.getSampleDataFn({ 
        dataSourceId, 
        collectionName, 
        limit 
      });
      return result.data as SampleDataResponse;
    } catch (error: any) {
      console.error('Error getting sample data:', error);
      throw new Error(error.message || 'Failed to get sample data');
    }
  }

  /**
   * Get user's data sources with filtering options
   */
  async getUserDataSources(
    organizationId: string,
    options?: {
      includePrivate?: boolean;
      type?: DataSourceType;
      status?: DataSourceStatus;
    }
  ): Promise<GetDataSourcesResponse> {
    try {
      const result = await this.listUserDataSourcesFn({ 
        organizationId,
        ...options
      });
      return result.data as GetDataSourcesResponse;
    } catch (error: any) {
      console.error('Error getting user data sources:', error);
      throw new Error(error.message || 'Failed to get data sources');
    }
  }

  /**
   * Delete a data source
   */
  async deleteDataSource(dataSourceId: string): Promise<DeleteDataSourceResponse> {
    try {
      const result = await this.deleteDataSourceFn({ dataSourceId });
      return result.data as DeleteDataSourceResponse;
    } catch (error: any) {
      console.error('Error deleting data source:', error);
      throw new Error(error.message || 'Failed to delete data source');
    }
  }

  /**
   * Update data source visibility and permissions
   */
  async updateVisibility(request: UpdateVisibilityRequest): Promise<UpdateVisibilityResponse> {
    try {
      const result = await this.updateVisibilityFn(request);
      return result.data as UpdateVisibilityResponse;
    } catch (error: any) {
      console.error('Error updating data source visibility:', error);
      throw new Error(error.message || 'Failed to update data source visibility');
    }
  }

  /**
   * Create a Firestore data source specifically (convenience method)
   */
  async createFirestoreDataSource(
    name: string,
    projectId: string,
    serviceAccountKey: string,
    organizationId: string,
    options?: {
      description?: string;
      visibility?: DataSourceVisibility;
      tags?: string[];
    }
  ): Promise<CreateDataSourceResponse> {
    return this.createDataSource({
      name,
      description: options?.description,
      type: 'firestore',
      visibility: options?.visibility || 'organization',
      configuration: {
        projectId,
        serviceAccountKey
      },
      tags: options?.tags,
      organizationId
    });
  }

  /**
   * Create a private data source (convenience method)
   */
  async createPrivateDataSource(
    name: string,
    type: DataSourceType,
    configuration: CreateDataSourceRequest['configuration'],
    organizationId: string,
    options?: {
      description?: string;
      tags?: string[];
    }
  ): Promise<CreateDataSourceResponse> {
    return this.createDataSource({
      name,
      description: options?.description,
      type,
      visibility: 'private',
      configuration,
      tags: options?.tags,
      organizationId
    });
  }

  /**
   * Share a private data source with organization
   */
  async shareWithOrganization(dataSourceId: string): Promise<UpdateVisibilityResponse> {
    return this.updateVisibility({
      dataSourceId,
      visibility: 'organization'
    });
  }

  /**
   * Make an organization data source private
   */
  async makePrivate(dataSourceId: string): Promise<UpdateVisibilityResponse> {
    return this.updateVisibility({
      dataSourceId,
      visibility: 'private'
    });
  }

  /**
   * Grant specific permissions to users
   */
  async grantPermissions(
    dataSourceId: string, 
    permissions: {
      read?: string[];
      write?: string[];
      admin?: string[];
    }
  ): Promise<UpdateVisibilityResponse> {
    return this.updateVisibility({
      dataSourceId,
      permissions
    });
  }

  /**
   * Check if user can perform actions on data source
   */
  canRead(dataSource: DataSource, userId: string): boolean {
    if (dataSource.userId === userId) return true;
    if (dataSource.visibility === 'private') return false;
    return dataSource.permissions.read.includes(userId) || 
           dataSource.permissions.write.includes(userId) || 
           dataSource.permissions.admin.includes(userId);
  }

  canWrite(dataSource: DataSource, userId: string): boolean {
    if (dataSource.userId === userId) return true;
    return dataSource.permissions.write.includes(userId) || 
           dataSource.permissions.admin.includes(userId);
  }

  canAdmin(dataSource: DataSource, userId: string): boolean {
    if (dataSource.userId === userId) return true;
    return dataSource.permissions.admin.includes(userId);
  }

  /**
   * Get user's role on a data source
   */
  getUserRole(dataSource: DataSource, userId: string): 'owner' | 'admin' | 'write' | 'read' | 'none' {
    if (dataSource.userId === userId) return 'owner';
    if (dataSource.permissions.admin.includes(userId)) return 'admin';
    if (dataSource.permissions.write.includes(userId)) return 'write';
    if (dataSource.permissions.read.includes(userId)) return 'read';
    return 'none';
  }

  /**
   * Get Google Analytics data
   */
  async getGoogleAnalyticsData(request: GoogleAnalyticsReportRequest): Promise<GoogleAnalyticsReportResponse> {
    try {
      const result = await this.getGoogleAnalyticsDataFn(request);
      return result.data as GoogleAnalyticsReportResponse;
    } catch (error: any) {
      console.error('Error getting Google Analytics data:', error);
      throw new Error(error.message || 'Failed to get Google Analytics data');
    }
  }
}

// Export a singleton instance
export const dataSourcesService = new DataSourcesService(); 